import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Icon } from "@iconify/react";
import { AppSettings } from "../../config/app-settings.js";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSubscriptionSuccessToast } from "../../components/toast/toast";
import axiosInstance, { CheckUserExpiry, setAuthTokens } from "../../components/content/axiosConfig.js";
import StripePaymentSection from "../../components/custom/StripePaymentSection.jsx";
import { normalizeCountryCode, COUNTRY_OPTIONS } from "../../utils/countries";
import { checkoutPageStyles } from "../../components/custom/checkoutPageStyles.js";

const WIZARD_STEPS = [
  { id: 1, label: "Account", hint: "Your details" },
  { id: 2, label: "Plan", hint: "Choose package" },
  { id: 3, label: "Payment", hint: "Secure checkout" },
];

const ACCOUNT_TYPE_OWNER = "owner";
const ACCOUNT_TYPE_COMPANY = "company";

function RegisterStepIndicator({ currentStep, accountType }) {
  const steps =
    accountType === ACCOUNT_TYPE_COMPANY
      ? [{ id: 1, label: "Account", hint: "Company details" }]
      : WIZARD_STEPS;
  return (
    <div className="pondstat-register-steps" aria-label="Registration progress">
      {steps.map((step) => {
        const done = currentStep > step.id;
        const active = currentStep === step.id;
        let stateClass = "";
        if (done) stateClass = " pondstat-register-step--done";
        else if (active) stateClass = " pondstat-register-step--active";

        return (
          <div key={step.id} className={`pondstat-register-step${stateClass}`}>
            <div className="pondstat-register-step__circle" aria-hidden="true">
              {done ? <Icon icon="solar:check-circle-bold" width={18} /> : step.id}
            </div>
            <div className="pondstat-register-step__label">{step.label}</div>
            <div className="pondstat-register-step__hint">{step.hint}</div>
          </div>
        );
      })}
    </div>
  );
}

function parseFeatures(features) {
  if (!features) return [];
  if (Array.isArray(features)) return features;
  try {
    const parsed = JSON.parse(features);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function computeCheckoutTotal(pkg, pondCount) {
  const included = pkg.IncludedPonds ?? 1;
  const extra = parseFloat(pkg.ExtraPondAmount) || 0;
  const count = pondCount ?? included;
  const base = parseFloat(pkg.Amount) || 0;
  return Math.round((base + Math.max(0, count - included) * extra) * 100) / 100;
}

function formatDurationLabel(duration) {
  if (duration === 12) return "/ year";
  return `/ ${duration} month${duration === 1 ? "" : "s"}`;
}

function isPackageActive(pkg) {
  return pkg.Status === true || pkg.Status === 1 || pkg.Status === "1";
}

function normalizePackageList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

function packageAllowsExtraPonds(pkg) {
  return pkg?.AllowsExtraPonds === true || pkg?.AllowsExtraPonds === 1;
}

/** Paid signup only — $0 packages cannot auto-bill after trial. */
function isPaidRegistrationPackage(pkg) {
  return pkg != null && Number(pkg.Amount) > 0;
}

function renderPackageDescription(description) {
  if (!description) return null;
  const hasHtml = /<[a-z][\s\S]*>/i.test(description);
  if (hasHtml) {
    return (
      <div
        className="pondstat-register-plan-card__desc"
        dangerouslySetInnerHTML={{ __html: description }}
      />
    );
  }
  return <p className="pondstat-register-plan-card__desc">{description}</p>;
}

/** Package feature strings may include simple HTML (e.g. W<sub>R</sub>). */
function renderPackageFeature(feature) {
  if (feature == null) return null;
  const text = String(feature);
  if (!/<sub>|<\/sub>|<sup>|<\/sup>/i.test(text)) return text;
  const sanitized = text.replace(/<(?!\/?(sub|sup)\b)[^>]*>/gi, "");
  return <span dangerouslySetInnerHTML={{ __html: sanitized }} />;
}

const RegisterSubscribeForm = () => {
  const history = useHistory();
  const location = useLocation();
  const context = useContext(AppSettings);
  const stripe = useStripe();
  const elements = useElements();

  const searchParams = new URLSearchParams(location.search);
  const packageIdFromUrl = searchParams.get("package");
  const pondsFromUrl = Math.max(1, parseInt(searchParams.get("ponds") || "1", 10));

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState(null);
  const [packagesError, setPackagesError] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(packageIdFromUrl || "");
  const [pondCount, setPondCount] = useState(pondsFromUrl);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreement, setAgreement] = useState(false);
  const [accountType, setAccountType] = useState(ACCOUNT_TYPE_OWNER);
  const [companyName, setCompanyName] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyCity, setCompanyCity] = useState("");
  const [companyState, setCompanyState] = useState("");
  const [companyPostalCode, setCompanyPostalCode] = useState("");
  const [companyEin, setCompanyEin] = useState("");
  const [country, setCountry] = useState("US");
  const [clientSecret, setClientSecret] = useState("");
  const [clientSecretType, setClientSecretType] = useState("payment");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [confirmedTotal, setConfirmedTotal] = useState(null);
  const [licensedPondCount, setLicensedPondCount] = useState(pondsFromUrl);
  const [recurringLabel, setRecurringLabel] = useState("");
  const [dueToday, setDueToday] = useState(null);
  const [renewalAmount, setRenewalAmount] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCouponCode, setAppliedCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponMessage, setCouponMessage] = useState("");
  const [couponError, setCouponError] = useState(false);
  const [originalTotal, setOriginalTotal] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountedTotal, setDiscountedTotal] = useState(null);
  const [trialDays, setTrialDays] = useState(null);
  const checkoutRequestId = useRef(0);
  const packagesRequestId = useRef(0);
  const subscriptionIdRef = useRef("");
  const emailRef = useRef(email);
  emailRef.current = email;
  const registrationFinalizedRef = useRef(false);

  const selectedPackage = packages.find((p) => String(p.id) === String(selectedPackageId));
  const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailValid = email.trim().length > 0 && emailRegex.test(email.trim());
  const isCompanySignup = accountType === ACCOUNT_TYPE_COMPANY;
  const accountFieldsValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    emailValid &&
    password.trim().length >= 8 &&
    agreement &&
    (!isCompanySignup ||
      (companyName.trim().length > 0 &&
        companyPhone.trim().length > 0 &&
        companyAddress.trim().length > 0 &&
        companyCity.trim().length > 0));
  const planStepValid = !!selectedPackage && !packagesError && packages.length > 0;

  const displayTotal =
    confirmedTotal ??
    (selectedPackage ? computeCheckoutTotal(selectedPackage, pondCount) : null);

  const stepSubtitle = isCompanySignup
    ? "Free company signup. Create and pay for pond owner accounts after you log in."
    : currentStep === 1
      ? "Tell us about yourself to get started."
      : currentStep === 2
        ? "Pick the plan that fits your pond management needs."
        : "Enter your card to start the free trial — you are charged when the trial ends.";

  const selectAccountType = (type) => {
    setAccountType(type);
    setError(null);
    if (type === ACCOUNT_TYPE_COMPANY && currentStep > 1) {
      setCurrentStep(1);
    }
  };

  const cancelRegistrationCheckout = useCallback(async (subId, emailAddr) => {
    if (!subId || !emailAddr?.trim()) return;
    try {
      await axiosInstance.post("api/cancel-registration-checkout", {
        subscriptionId: subId,
        email: emailAddr.trim(),
      });
    } catch {
      // Best-effort orphan cleanup — sweeper covers leftovers.
    }
  }, []);

  const cancelRegistrationCheckoutBeacon = useCallback((subId, emailAddr) => {
    if (!subId || !emailAddr?.trim()) return;
    try {
      const base = String(axiosInstance.defaults.baseURL || "").replace(/\/$/, "");
      const url = `${base}/api/cancel-registration-checkout`;
      const body = JSON.stringify({
        subscriptionId: subId,
        email: emailAddr.trim(),
      });
      if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(url, blob);
        return;
      }
      cancelRegistrationCheckout(subId, emailAddr);
    } catch {
      // ignore
    }
  }, [cancelRegistrationCheckout]);

  const loadPackages = useCallback(async () => {
    const requestId = ++packagesRequestId.current;
    setLoading(true);
    setPackagesError(null);

    try {
      const response = await axiosInstance.get("api/Packages/GetAll");
      if (requestId !== packagesRequestId.current) return;

      const activePackages = normalizePackageList(response.data)
        .filter(isPackageActive)
        .filter(isPaidRegistrationPackage);
      setPackages(activePackages);

      if (activePackages.length === 0) {
        const message = "No subscription plans are available right now. Please try again later.";
        setPackagesError(message);
        showErrorToast(message);
        return;
      }

      if (packageIdFromUrl && activePackages.some((p) => String(p.id) === String(packageIdFromUrl))) {
        setSelectedPackageId(String(packageIdFromUrl));
      } else {
        setSelectedPackageId(String(activePackages[0].id));
      }
    } catch (err) {
      if (requestId !== packagesRequestId.current) return;
      const message =
        err.response?.data?.message ||
        err.message ||
        "Unable to load subscription plans";
      setPackagesError(message);
      showErrorToast(message);
    } finally {
      if (requestId === packagesRequestId.current) {
        setLoading(false);
      }
    }
  }, [packageIdFromUrl]);

  const refreshCheckout = useCallback(async (couponToApply = "") => {
    if (!selectedPackageId || !emailValid || !fullName) {
      setClientSecret("");
      setSubscriptionId("");
      setConfirmedTotal(null);
      return;
    }

    const requestId = ++checkoutRequestId.current;
    setCheckoutLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post("api/create-registration-checkout", {
        email: email.trim(),
        fullName,
        packageId: parseInt(selectedPackageId, 10),
        pondCount,
        country: normalizeCountryCode(country),
        couponCode: couponToApply || undefined,
        replaceSubscriptionId: subscriptionIdRef.current || undefined,
      });

      if (requestId !== checkoutRequestId.current) return;

      const data = response.data;
      if (!data.clientSecret) {
        throw new Error("Failed to initialize payment");
      }

      setClientSecret(data.clientSecret);
      setClientSecretType(data.clientSecretType || "payment");
      setSubscriptionId(data.subscriptionId || "");
      subscriptionIdRef.current = data.subscriptionId || "";
      setConfirmedTotal(data.amount);
      setOriginalTotal(data.originalTotal ?? data.amount);
      setDiscountAmount(data.discountAmount ?? 0);
      setDiscountedTotal(data.discountedTotal ?? data.amount);
      setLicensedPondCount(data.licensedPondCount ?? pondCount);
      setRecurringLabel(data.recurringLabel || "");
      setDueToday(data.dueToday ?? data.amount ?? null);
      setRenewalAmount(data.renewalAmount ?? data.amount ?? null);
      setTrialDays(data.trialDays ?? null);
      if (data.customerCountry) {
        setCountry(data.customerCountry);
      }
    } catch (err) {
      if (requestId !== checkoutRequestId.current) return;
      const message = err.response?.data?.error || err.message || "Could not prepare checkout";
      setError(message);
      setClientSecret("");
      setSubscriptionId("");
      subscriptionIdRef.current = "";
    } finally {
      if (requestId === checkoutRequestId.current) {
        setCheckoutLoading(false);
      }
    }
  }, [selectedPackageId, emailValid, fullName, email, pondCount, country]);

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code || !selectedPackageId) return;

    setCouponLoading(true);
    setCouponMessage("");
    setCouponError(false);
    setError(null);

    try {
      const validateRes = await axiosInstance.post("api/Coupons/Validate", {
        code,
        packageId: parseInt(selectedPackageId, 10),
        pondCount,
      });

      if (!validateRes.data?.valid) {
        setCouponError(true);
        setCouponMessage(validateRes.data?.message || "Invalid coupon code");
        setAppliedCouponCode("");
        return;
      }

      await refreshCheckout(code);
      setAppliedCouponCode(code);
      setCouponMessage(validateRes.data.message || "Coupon applied successfully");
      setCouponError(false);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Could not apply coupon";
      setAppliedCouponCode("");
      setCouponError(true);
      setCouponMessage(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setCouponLoading(false);
    }
  };

  useEffect(() => {
    const {
      handleSetAppSidebarNone,
      handleSetAppHeaderNone,
      handleSetAppContentClass,
    } = context;
    handleSetAppSidebarNone(true);
    handleSetAppHeaderNone(true);
    handleSetAppContentClass("p-0");
    // Do not re-enable chrome on unmount — Content.jsx owns shell for auth routes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  useEffect(() => {
    if (currentStep !== 3) return undefined;
    const timer = setTimeout(() => {
      refreshCheckout(appliedCouponCode);
    }, 400);
    return () => clearTimeout(timer);
  }, [refreshCheckout, currentStep, appliedCouponCode]);

  useEffect(() => {
    if (!selectedPackage || packageAllowsExtraPonds(selectedPackage)) return;
    setPondCount(selectedPackage?.IncludedPonds ?? 1);
  }, [selectedPackage]);

  useEffect(() => {
    const onBeforeUnload = () => {
      if (registrationFinalizedRef.current) return;
      const subId = subscriptionIdRef.current;
      if (!subId) return;
      cancelRegistrationCheckoutBeacon(subId, emailRef.current);
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      // Page leave / SPA navigate away — not step Back (that clears the ref first).
      if (registrationFinalizedRef.current) return;
      const subId = subscriptionIdRef.current;
      if (!subId) return;
      cancelRegistrationCheckout(subId, emailRef.current);
    };
  }, [cancelRegistrationCheckout, cancelRegistrationCheckoutBeacon]);

  const goToNextStep = () => {
    setError(null);
    if (currentStep === 1) {
      if (!accountFieldsValid) {
        showErrorToast("Please complete all required fields and accept the terms.");
        return;
      }
      if (isCompanySignup) {
        void registerCompanyAccount();
        return;
      }
      setCurrentStep(2);
      return;
    }
    if (currentStep === 2) {
      if (!planStepValid) {
        showErrorToast("Please select a subscription plan to continue.");
        return;
      }
      setCurrentStep(3);
    }
  };

  const registerCompanyAccount = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const companyRes = await axiosInstance.post("api/company/Register", {
        CompanyName: companyName.trim(),
        FullName: fullName,
        Email: email.trim(),
        Password: password.trim(),
        Phone: companyPhone.trim(),
        Address: companyAddress.trim(),
        City: companyCity.trim(),
        State: companyState.trim() || undefined,
        PostalCode: companyPostalCode.trim() || undefined,
        EIN: companyEin.trim() || undefined,
        Country: normalizeCountryCode(country) || "US",
      });
      const { token, refreshToken, User } = companyRes.data || {};
      if (!token || !User) {
        throw new Error("Company created but session could not be started");
      }
      registrationFinalizedRef.current = true;
      setAuthTokens(token, refreshToken, User);
      await CheckUserExpiry();
      localStorage.setItem("isExpired", "false");
      showSubscriptionSuccessToast(
        companyRes.data?.message || "Pond Management Company created. Welcome!"
      );
      history.push("/company/accounts");
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Company registration failed";
      setError(message);
      showErrorToast(message);
    } finally {
      setSubmitting(false);
    }
  };

  const goToPreviousStep = () => {
    setError(null);
    if (currentStep === 3) {
      const subId = subscriptionIdRef.current;
      const emailAddr = email.trim();
      setClientSecret("");
      setSubscriptionId("");
      subscriptionIdRef.current = "";
      setConfirmedTotal(null);
      setAppliedCouponCode("");
      setCouponMessage("");
      setCouponError(false);
      if (subId) {
        cancelRegistrationCheckout(subId, emailAddr);
      }
    }
    setCurrentStep((s) => Math.max(1, s - 1));
  };

  const finishRegistrationSession = async (registerResponse) => {
    registrationFinalizedRef.current = true;
    subscriptionIdRef.current = "";
    const { token, refreshToken, User } = registerResponse.data;
    setAuthTokens(token, refreshToken, User);
    await CheckUserExpiry();
    localStorage.setItem("isExpired", "false");

    const notification =
      registerResponse.data?.notification ||
      registerResponse.data?.message ||
      "Welcome to PondStat! Your account is ready.";
    showSubscriptionSuccessToast(notification);
    history.push("/dashboard");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (currentStep !== 3) {
      goToNextStep();
      return;
    }

    if (!accountFieldsValid) {
      showErrorToast("Please complete all required fields and accept the terms.");
      setCurrentStep(1);
      return;
    }

    const billingCountry = normalizeCountryCode(country);

    if (!stripe || !elements || !clientSecret) {
      const message = checkoutLoading
        ? "Payment is still loading. Please wait a moment."
        : "Payment system is not ready. Please try again.";
      setError(message);
      showErrorToast(message);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      const message = "Payment form is not loaded. Please try again.";
      setError(message);
      showErrorToast(message);
      return;
    }

    setSubmitting(true);
    let cardConfirmed = false;
    try {
      const billingDetails = {
        name: fullName,
        email: email.trim(),
        address: { country: billingCountry },
      };
      const isSetupCheckout = clientSecretType === "setup" || dueToday === 0;
      let confirmedIntentId = null;

      if (isSetupCheckout) {
        const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: billingDetails,
          },
        });

        if (stripeError) {
          const message = `Payment failed: ${stripeError.message}`;
          setError(message);
          showErrorToast(message);
          return;
        }

        if (setupIntent.status !== "succeeded") {
          const message = "Payment method was not confirmed. Please try again.";
          setError(message);
          showErrorToast(message);
          return;
        }

        confirmedIntentId = setupIntent.id;
      } else {
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: billingDetails,
          },
          receipt_email: email.trim(),
        });

        if (stripeError) {
          const message = `Payment failed: ${stripeError.message}`;
          setError(message);
          showErrorToast(message);
          return;
        }

        if (paymentIntent.status !== "succeeded") {
          const message = "Payment was not completed. Please try again.";
          setError(message);
          showErrorToast(message);
          return;
        }

        confirmedIntentId = paymentIntent.id;
      }

      cardConfirmed = true;

      const registerResponse = await axiosInstance.post("api/fisher/RegisterWithPayment", {
        FullName: fullName,
        Email: email.trim(),
        Password: password.trim(),
        PackageID: parseInt(selectedPackageId, 10),
        LicensedPondCount: licensedPondCount,
        AmountPaid:
          dueToday === 0 ? 0 : discountedTotal ?? confirmedTotal ?? displayTotal,
        StripePaymentIntentId: confirmedIntentId,
        StripeSubscriptionId: subscriptionId,
        Country: billingCountry,
      });

      await finishRegistrationSession(registerResponse);
    } catch (err) {
      const status = err.response?.status;
      // Server cancels when no fisher was persisted. Only belt-and-suspenders on 4xx
      // so we never cancel after a partial create that returned 5xx.
      if (cardConfirmed && status >= 400 && status < 500) {
        const subId = subscriptionIdRef.current || subscriptionId;
        await cancelRegistrationCheckout(subId, email.trim());
        subscriptionIdRef.current = "";
        setSubscriptionId("");
        setClientSecret("");
      }
      const message = err.response?.data?.message || err.message || "Registration failed";
      setError(message);
      showErrorToast(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Preloader />;
  }

  const busy = submitting || checkoutLoading || !stripe || !elements;
  const paymentReady = !!(stripe && elements && clientSecret);
  const couponApplied = !!appliedCouponCode;
  const paidTrialLabel =
    trialDays != null && trialDays > 0
      ? `${trialDays}-day free trial, then billed automatically`
      : recurringLabel || "Billed after checkout";

  return (
    <form onSubmit={handleSubmit}>
      <style>{checkoutPageStyles}</style>
      <div
        className="pondstat-register-page pondstat-checkout-page"
        style={{ minHeight: "100vh" }}
      >
        <div className="pondstat-register-wizard">
          <header className="pondstat-register-wizard__header">
            <div className="pondstat-register-wizard__logo">
              <span style={{ color: "#111827" }}>POND</span>
              <span style={{ color: "#4ECDC4" }}>STAT</span>
            </div>
            <h1 className="pondstat-register-wizard__title">Create your account</h1>
            <p className="pondstat-register-wizard__subtitle">{stepSubtitle}</p>
          </header>

          <RegisterStepIndicator currentStep={currentStep} accountType={accountType} />

          {packagesError && currentStep >= 2 && !isCompanySignup && (
            <div
              className="alert alert-danger d-flex align-items-start gap-3 mb-3"
              role="alert"
              style={{ borderRadius: 12 }}
            >
              <Icon icon="solar:danger-circle-bold" width={22} className="flex-shrink-0 mt-1" />
              <div className="flex-grow-1">
                <div className="fw-semibold mb-1">Could not load plans</div>
                <div className="small mb-2">{packagesError}</div>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={loadPackages}>
                  Try again
                </button>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="pondstat-register-card">
              <h2 className="pondstat-register-card__heading">Account information</h2>
              <p className="pondstat-register-card__desc">
                {isCompanySignup
                  ? "Create your free Pond Management Company account. You can add and pay for pond owner accounts after you sign in."
                  : "We'll use this to set up your PondStat login and send your receipt."}
              </p>

              {error && (
                <div
                  className="alert alert-danger d-flex align-items-start gap-3 mb-3"
                  role="alert"
                  style={{ borderRadius: 12 }}
                >
                  <Icon icon="solar:danger-circle-bold" width={22} className="flex-shrink-0 mt-1" />
                  <div className="flex-grow-1">
                    <div className="fw-semibold mb-1">Could not create account</div>
                    <div className="small mb-0">{error}</div>
                  </div>
                </div>
              )}

              <div className="pondstat-register-field" style={{ marginBottom: 20 }}>
                <label className="d-block mb-2">Account type *</label>
                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={`btn btn-sm rounded-pill px-3 ${
                      accountType === ACCOUNT_TYPE_OWNER ? "btn-primary" : "btn-outline-secondary"
                    }`}
                    onClick={() => selectAccountType(ACCOUNT_TYPE_OWNER)}
                  >
                    Pond Owner
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm rounded-pill px-3 ${
                      accountType === ACCOUNT_TYPE_COMPANY ? "btn-primary" : "btn-outline-secondary"
                    }`}
                    onClick={() => selectAccountType(ACCOUNT_TYPE_COMPANY)}
                  >
                    Pond Management Company
                  </button>
                </div>
              </div>

              {isCompanySignup && (
                <>
                  <div className="pondstat-register-field">
                    <label htmlFor="reg-company-name">Company name *</label>
                    <input
                      id="reg-company-name"
                      type="text"
                      placeholder="Company name"
                      required
                      autoComplete="organization"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                    />
                  </div>

                  <div className="pondstat-register-field">
                    <label htmlFor="reg-company-phone">Phone *</label>
                    <input
                      id="reg-company-phone"
                      type="tel"
                      placeholder="Business phone"
                      required
                      autoComplete="tel"
                      value={companyPhone}
                      onChange={(e) => setCompanyPhone(e.target.value)}
                    />
                  </div>

                  <div className="pondstat-register-field">
                    <label htmlFor="reg-company-address">Street address *</label>
                    <input
                      id="reg-company-address"
                      type="text"
                      placeholder="Street address"
                      required
                      autoComplete="street-address"
                      value={companyAddress}
                      onChange={(e) => setCompanyAddress(e.target.value)}
                    />
                  </div>

                  <div className="pondstat-register-field-row pondstat-register-field">
                    <div>
                      <label htmlFor="reg-company-city">City *</label>
                      <input
                        id="reg-company-city"
                        type="text"
                        placeholder="City"
                        required
                        autoComplete="address-level2"
                        value={companyCity}
                        onChange={(e) => setCompanyCity(e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor="reg-company-state">State / region</label>
                      <input
                        id="reg-company-state"
                        type="text"
                        placeholder="State or region"
                        autoComplete="address-level1"
                        value={companyState}
                        onChange={(e) => setCompanyState(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="pondstat-register-field-row pondstat-register-field">
                    <div>
                      <label htmlFor="reg-company-postal">Postal / ZIP code</label>
                      <input
                        id="reg-company-postal"
                        type="text"
                        placeholder="ZIP or postal code"
                        autoComplete="postal-code"
                        value={companyPostalCode}
                        onChange={(e) => setCompanyPostalCode(e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor="reg-company-country">Country *</label>
                      <select
                        id="reg-company-country"
                        required
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                      >
                        {COUNTRY_OPTIONS.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="pondstat-register-field">
                    <label htmlFor="reg-company-ein">EIN (optional)</label>
                    <input
                      id="reg-company-ein"
                      type="text"
                      placeholder="Employer Identification Number"
                      autoComplete="off"
                      value={companyEin}
                      onChange={(e) => setCompanyEin(e.target.value)}
                    />
                    <div className="form-text text-muted small mt-1">
                      Optional — for business / tax tracking if you have one.
                    </div>
                  </div>
                </>
              )}

              <div className="pondstat-register-field-row pondstat-register-field">
                <div>
                  <label htmlFor="reg-first-name">
                    {isCompanySignup ? "Manager first name *" : "First name *"}
                  </label>
                  <input
                    id="reg-first-name"
                    type="text"
                    placeholder="First name"
                    required
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="reg-last-name">
                    {isCompanySignup ? "Manager last name *" : "Last name *"}
                  </label>
                  <input
                    id="reg-last-name"
                    type="text"
                    placeholder="Last name"
                    required
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              <div className="pondstat-register-field">
                <label htmlFor="reg-email">Work email *</label>
                <input
                  id="reg-email"
                  type="email"
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                />
              </div>

              <div className="pondstat-register-field">
                <label htmlFor="reg-password">Password *</label>
                <input
                  id="reg-password"
                  type="password"
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="agreementCheckbox"
                  checked={agreement}
                  onChange={(e) => setAgreement(e.target.checked)}
                  required
                />
                <label className="form-check-label small text-muted" htmlFor="agreementCheckbox">
                  I agree to the{" "}
                  <Link to="#" style={{ color: "#2b7a78", textDecoration: "none" }}>
                    Terms
                  </Link>{" "}
                  and{" "}
                  <Link to="#" style={{ color: "#2b7a78", textDecoration: "none" }}>
                    Data Policy
                  </Link>
                  .
                </label>
              </div>

              <div className="pondstat-register-nav" style={{ marginTop: 24 }}>
                <button
                  type="submit"
                  className="pondstat-register-nav__next"
                  disabled={!accountFieldsValid || submitting}
                >
                  {submitting
                    ? isCompanySignup
                      ? "Creating account…"
                      : "Please wait…"
                    : isCompanySignup
                      ? "Create company account"
                      : "Continue to plan"}
                  {!submitting && <Icon icon="solar:arrow-right-linear" width={20} />}
                </button>
              </div>
            </div>
          )}

          {currentStep === 2 && !isCompanySignup && (
            <div className="pondstat-register-card">
              <h2 className="pondstat-register-card__heading">Choose your plan</h2>
              <p className="pondstat-register-card__desc">
                Select a subscription package. You can adjust licensed ponds where available.
              </p>

              <div className="pondstat-register-plan-grid" role="radiogroup" aria-label="Subscription plans">
                {packages.map((pkg) => {
                  const selected = String(pkg.id) === String(selectedPackageId);
                  const features = parseFeatures(pkg.Features);
                  const allowsExtra = packageAllowsExtraPonds(pkg);
                  const extraPerPond = parseFloat(pkg.ExtraPondAmount) || 0;
                  const cardTotal = computeCheckoutTotal(pkg, pondCount);
                  return (
                    <div
                      key={pkg.id}
                      className={`pondstat-register-plan-card-wrap${
                        selected ? " pondstat-register-plan-card-wrap--selected" : ""
                      }`}
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className={`pondstat-register-plan-card${selected ? " pondstat-register-plan-card--selected" : ""}`}
                        onClick={() => setSelectedPackageId(String(pkg.id))}
                      >
                        <div className="d-flex gap-3">
                          <div className="pondstat-register-plan-card__radio" aria-hidden="true" />
                          <div className="flex-grow-1">
                            <div className="pondstat-register-plan-card__top">
                              <h3 className="pondstat-register-plan-card__name">{pkg.Name}</h3>
                              <div>
                                <div className="pondstat-register-plan-card__price">
                                  ${Number(pkg.Amount || 0).toFixed(2)}
                                  <small>{formatDurationLabel(pkg.Duration || 12)}</small>
                                </div>
                                {allowsExtra && extraPerPond > 0 && (
                                  <div className="pondstat-register-plan-card__extra-price">
                                    +${extraPerPond.toFixed(2)}/extra pond
                                  </div>
                                )}
                              </div>
                            </div>
                            {renderPackageDescription(pkg.Description)}
                            {!allowsExtra && (
                              <p className="pondstat-register-plan-card__pond-note">Includes 1 pond</p>
                            )}
                            {features.length > 0 && (
                              <ul className="pondstat-register-plan-card__features">
                                {features.slice(0, 5).map((f) => (
                                  <li key={f}>
                                    <Icon icon="solar:check-circle-bold" className="text-success" width={16} />
                                    {renderPackageFeature(f)}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </button>

                      {selected && allowsExtra && (
                        <div
                          className="pondstat-register-plan-card__pond-picker"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <label htmlFor={`reg-pond-count-${pkg.id}`}>Licensed ponds</label>
                          <input
                            id={`reg-pond-count-${pkg.id}`}
                            type="number"
                            min={pkg.IncludedPonds ?? 1}
                            value={pondCount}
                            onChange={(e) =>
                              setPondCount(
                                Math.max(pkg.IncludedPonds ?? 1, parseInt(e.target.value, 10) || 1)
                              )
                            }
                          />
                          {extraPerPond > 0 && (
                            <p className="pondstat-register-plan-card__pond-picker-hint">
                              {pkg.IncludedPonds ?? 1} pond
                              {(pkg.IncludedPonds ?? 1) === 1 ? "" : "s"} included. Additional ponds
                              billed at ${extraPerPond.toFixed(2)} each.
                            </p>
                          )}
                          {pondCount > (pkg.IncludedPonds ?? 1) && (
                            <p className="pondstat-register-plan-card__pond-total">
                              Total <strong>${cardTotal.toFixed(2)}</strong>
                              {formatDurationLabel(pkg.Duration || 12)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {selectedPackage && (
                <div className="pondstat-register-order-strip" style={{ marginTop: 20 }}>
                  <div>
                    <p className="pondstat-register-order-strip__plan">{selectedPackage.Name}</p>
                    <p className="pondstat-register-order-strip__meta">
                      {packageAllowsExtraPonds(selectedPackage)
                        ? `${pondCount} licensed pond${pondCount === 1 ? "" : "s"}`
                        : "1 pond included"}{" "}
                      · {paidTrialLabel}
                    </p>
                  </div>
                  <div className="pondstat-register-order-strip__total">
                    ${Number(displayTotal || 0).toFixed(2)}
                    <small>{formatDurationLabel(selectedPackage.Duration || 12)}</small>
                  </div>
                </div>
              )}

              <div className="pondstat-register-nav">
                <button type="button" className="pondstat-register-nav__back" onClick={goToPreviousStep}>
                  Back
                </button>
                <button
                  type="submit"
                  className="pondstat-register-nav__next"
                  disabled={!planStepValid}
                >
                  Continue to payment
                  <Icon icon="solar:arrow-right-linear" width={20} />
                </button>
              </div>
            </div>
          )}

          {currentStep === 3 && !isCompanySignup && selectedPackage && (
            <>
              <div className="pondstat-register-order-strip">
                <div>
                  <p className="pondstat-register-order-strip__plan">{selectedPackage.Name}</p>
                  <p className="pondstat-register-order-strip__meta">
                    {fullName} · {email.trim()} ·{" "}
                    {(licensedPondCount || pondCount) === 1
                      ? "1 pond"
                      : `${licensedPondCount || pondCount} ponds`}
                    {trialDays != null && trialDays > 0
                      ? ` · ${trialDays}-day trial, then auto-billed`
                      : ""}
                  </p>
                </div>
                <div className="pondstat-register-order-strip__total">
                  {dueToday === 0 ? (
                    <>
                      $0.00
                      <small> due today</small>
                    </>
                  ) : couponApplied && originalTotal != null ? (
                    <>
                      <span className="text-muted text-decoration-line-through me-2">
                        ${Number(originalTotal).toFixed(2)}
                      </span>
                      ${Number((discountedTotal ?? displayTotal) || 0).toFixed(2)}
                      <small>{formatDurationLabel(selectedPackage.Duration || 12)}</small>
                    </>
                  ) : (
                    <>
                      ${Number(displayTotal || 0).toFixed(2)}
                      <small>{formatDurationLabel(selectedPackage.Duration || 12)}</small>
                    </>
                  )}
                </div>
              </div>

              <StripePaymentSection
                amount={dueToday ?? discountedTotal ?? displayTotal}
                renewalAmount={renewalAmount ?? displayTotal}
                dueToday={dueToday}
                loading={busy}
                disabled={!paymentReady || !accountFieldsValid || !!packagesError}
                error={error || packagesError}
                submitLabel={
                  dueToday === 0
                    ? `Start ${trialDays || 14}-day free trial`
                    : displayTotal != null
                      ? `Create account & pay $${Number(discountedTotal ?? displayTotal).toFixed(2)}`
                      : "Create account & subscribe"
                }
                loadingLabel={
                  submitting
                    ? "Creating your account..."
                    : !stripe || !elements
                      ? "Loading payment form..."
                      : "Preparing checkout..."
                }
                customerName={fullName || undefined}
                customerEmail={email.trim() || undefined}
                country={country}
                onCountryChange={setCountry}
                recurringLabel={recurringLabel}
                showCountry={false}
                onCancel={goToPreviousStep}
                cancelLabel="Back to plan"
                showCoupon
                couponCode={couponCode}
                onCouponCodeChange={setCouponCode}
                onApplyCoupon={handleApplyCoupon}
                couponLoading={couponLoading}
                couponMessage={couponMessage}
                couponApplied={couponApplied}
                couponError={couponError}
                originalTotal={originalTotal}
                discountAmount={discountAmount}
                discountedTotal={discountedTotal}
              />
            </>
          )}

          <div className="pondstat-register-footer">
            Already have an account? <Link to="/user/login">Log in</Link>
          </div>

          <div className="pondstat-register-copyright">&copy; PONDSTAT. All Rights Reserved</div>
        </div>
      </div>
    </form>
  );
};

export default RegisterSubscribeForm;
